import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { refreshSections } from "@/hooks/useSections";
import { Eye, EyeOff, Save, Loader2 } from "lucide-react";

const SECTION_GROUPS = [
  {
    title: "Home page",
    sections: [
      { key: "home.showcase", label: "Before/after showcase card" },
      { key: "home.quote", label: "Free-estimate CTA card" },
      { key: "home.process", label: '"How it works" steps' },
      { key: "home.cta", label: "Final CTA block" },
    ],
  },
  {
    title: "Portfolio page",
    sections: [
      { key: "portfolio.items", label: "Portfolio gallery (the cards)" },
      { key: "portfolio.cta", label: "Bottom CTA block" },
    ],
  },
  {
    title: "About page",
    sections: [
      { key: "about.stats", label: "Stat blocks (16 / 1 / 100%)" },
      { key: "about.body", label: "Story paragraphs + signature" },
      { key: "about.personal", label: "Personal blurb (\"outside the truck\")" },
      { key: "about.cta", label: "Bottom CTA block" },
    ],
  },
  {
    title: "Footer",
    sections: [
      { key: "footer.tagline", label: "Tagline paragraph (left column)" },
      { key: "footer.hours", label: "Business hours block" },
    ],
  },
];

export default function SectionsTab() {
  const [sections, setSections] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sections");
      setSections(data);
      setOriginal(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changedKeys = Object.keys(sections).filter((k) => sections[k] !== original[k]);
  const dirty = changedKeys.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const updates = Object.fromEntries(changedKeys.map((k) => [k, sections[k]]));
      await api.put("/sections", { updates }, { withCredentials: true });
      await refreshSections();
      setOriginal({ ...sections });
      toast.success(`Updated ${changedKeys.length} section${changedKeys.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-24 bg-muted rounded-xl animate-pulse" />;

  return (
    <div data-testid="sections-tab" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10 bg-background/80 backdrop-blur py-3 -mx-1 px-1">
        <div className="min-w-0">
          <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">Show / hide sections</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Toggle any section off to instantly hide it from customers. Toggle back on whenever you want.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="sections-save"
          className="btn-primary rounded-full px-5 shrink-0"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save {dirty && `(${changedKeys.length})`}
        </Button>
      </div>

      {SECTION_GROUPS.map((group) => (
        <Card key={group.title} className="p-6 border-border bg-white">
          <h3 className="font-display text-lg font-semibold tracking-tight">{group.title}</h3>
          {group.description && (
            <p className="text-sm text-muted-foreground mt-1 mb-3">{group.description}</p>
          )}
          <div className={`space-y-1 ${group.description ? "" : "mt-4"}`}>
            {group.sections.map((s) => {
              const on = sections[s.key] !== false;
              return (
                <div
                  key={s.key}
                  data-testid={`section-row-${s.key}`}
                  className="flex items-center justify-between py-3 border-b border-border last:border-0"
                >
                  <Label htmlFor={s.key} className="flex items-center gap-3 cursor-pointer">
                    {on ? <Eye className="w-4 h-4 text-primary" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                    <span className={`text-sm ${on ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                  </Label>
                  <Switch
                    id={s.key}
                    data-testid={`section-toggle-${s.key}`}
                    checked={on}
                    onCheckedChange={(v) => setSections({ ...sections, [s.key]: v })}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
